import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import EmployeePeek from "@/components/EmployeePeek";
import ImportEmployeesDialog from "@/components/ImportEmployeesDialog";
import { FieldError, invalidClass } from "@/components/FieldError";
import { useFieldErrors } from "@/lib/formErrors";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import Paginator, { usePaged } from "@/components/Paginator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Plus, KeyRound, Upload, Pencil, Trash2, Undo2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import CopyableSecret from "@/components/CopyableSecret";
import { useSearch } from "@/lib/search";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FieldLabel } from "@/components/FieldLabel";
import ComboBox from "@/components/ComboBox";
import SortableTh, { useSorted } from "@/components/SortableTh";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// The boxes both dialogs draw, by the server's names.
const EMP_FIELDS = ["employee_code", "full_name", "designation", "email", "mobile", "date_of_joining", "unit_id", "department_id"];
const REQUIRED = { employee_code: "Employee code", full_name: "Full name", unit_id: "Unit", department_id: "Department" };
const missingIn = (form) => Object.fromEntries(
  Object.entries(REQUIRED).filter(([k]) => !String(form[k] ?? "").trim()).map(([k, label]) => [k, `${label} is required`]));

// Module scope: a stable reference, or the memo is rebuilt every render.
const EMPLOYEE_FIELDS = ["full_name", "employee_code", "designation", "email", "mobile"];

// How each sortable column is read off an employee row.
const EMP_SORT = {
  code: "employee_code", name: "full_name", designation: "designation",
  department: (e, ctx) => ctx.deptName(e.department_id),
  unit: (e, ctx) => ctx.unitName(e.unit_id),
  email: "email",
  login: (e) => (e.has_login ? 1 : 0),
};

const BLANK = { employee_code: "", full_name: "", designation: "", email: "", mobile: "", unit_id: "", department_id: "", date_of_joining: "" };

/**
 * The departments a person posted to this unit can actually belong to.
 *
 * `departments.unit_id` is NOT NULL — a department belongs to exactly one plant — so
 * offering all of them let somebody in Vadodara be filed under Ankleshwar's Quality.
 * The Excel import had always refused that row; only the form accepted it.
 *
 * `keep` is the department already on the record: an employee saved before this was
 * enforced still has to be visible in their own edit dialog, or opening it would appear
 * to have silently changed their department.
 */
function departmentsInUnit(depts, unitId, keep) {
  if (!unitId) return [];
  return depts.filter((d) => d.unit_id === unitId || (keep && d.id === keep));
}

export default function Employees() {
  const { user } = useAuth();
  const writable = canWrite(user);
  // null until the register has answered, so no count is printed for a list that never arrived.
  const [emps, setEmps] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [unit, setUnit] = useState("all");
  const [dept, setDept] = useState("all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [editing, setEditing] = useState(null);   // { row, form }
  const [deleting, setDeleting] = useState(null);
  const [retired, setRetired] = useState(null);   // null until asked for
  const [busy, setBusy] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [cred, setCred] = useState(null);
  // Designations HR has already used. A suggestion, not a rule — a new role still has to
  // be typeable — but it stops "Sr. Engineer", "Senior Engineer" and "Sr Engineer" from
  // becoming three different things in the same register.
  const [hints, setHints] = useState({ designations: [] });
  const [sort, setSort] = useState("");
  const addErr = useFieldErrors();
  const editErr = useFieldErrors();
  // Which person's panel is open, in the URL so a refresh or a pasted link reopens it.
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("open") || "";
  const setOpenId = (id) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    if (id) next.set("open", id); else next.delete("open");
    return next;
  }, { replace: true });
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();

  // Settled one by one. Under Promise.all a failed /suggestions — which only supplies the
  // designation hints — took the register down with it, and the page read "0 people
  // across all units." over an empty table. A row is drawn from the register, its units
  // and its departments, so without any of those it says the register could not be read.
  // A 200 counts only when it is a list: an HTML page answering in place of one was taken
  // as the register, and the page showed no sign that anything went wrong.
  // Returns whether it was read, so a write can say when the list behind it did not catch
  // up — or null when a later load has overtaken it. Loads are numbered because a write's
  // reload can overlap the one before it, and a slow failure landing after a good answer
  // would otherwise wipe the rows that answer drew.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoadError("");
    const [e, u, d, s] = await Promise.allSettled([
      api.get("/employees"), api.get("/units"), api.get("/departments"), api.get("/suggestions")]);
    if (seq !== loadSeq.current) return null;
    const isList = (r) => r.status === "fulfilled" && Array.isArray(r.value.data);
    if (isList(u)) setUnits(u.value.data);
    if (isList(d)) setDepts(d.value.data);
    if (s.status === "fulfilled") setHints(s.value.data);
    const failed = [e, u, d].find((r) => !isList(r));
    if (!failed) { setEmps(e.value.data); return true; }
    // The old rows go too: after a write they are exactly what the success toast has just
    // said is no longer true.
    setEmps(null);
    setLoadError(formatApiError(failed.reason?.response?.data?.detail, "The employee register could not be loaded."));
    return false;
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    if ((await load()) === false) toast.error("Saved, but the list could not be refreshed");
  };

  // Changing the unit clears a department belonging to the old one, rather than leaving
  // a selection that is no longer on the list and that the server will now refuse.
  const setAddUnit = (unit_id) => setF((s) => ({
    ...s, unit_id,
    department_id: departmentsInUnit(depts, unit_id).some((d) => d.id === s.department_id)
      ? s.department_id : "",
  }));

  const setEditUnit = (unit_id) => setEditing((e) => ({
    ...e,
    form: {
      ...e.form, unit_id,
      department_id: departmentsInUnit(depts, unit_id).some((d) => d.id === e.form.department_id)
        ? e.form.department_id : "",
    },
  }));

  const submit = async () => {
    const gaps = missingIn(f);
    if (Object.keys(gaps).length) { addErr.report({ fields: gaps }); return; }
    try {
      await api.post("/employees", f);
      toast.success("Employee added");
      setOpen(false);
      setF(BLANK);
      addErr.clear();
      refresh();
    } catch (e) { addErr.report(e, { known: EMP_FIELDS }); }
  };

  // Typing into a box that was refused clears its message; the next save re-checks.
  const setAdd = (patch) => { Object.keys(patch).forEach(addErr.clear); setF((s) => ({ ...s, ...patch })); };
  const setEdit = (patch) => { Object.keys(patch).forEach(editErr.clear); setEditing((e) => ({ ...e, form: { ...e.form, ...patch } })); };

  const createLogin = async (emp) => {
    try {
      const r = await api.post(`/employees/${emp.id}/create-login`);
      setCred({ ...r.data, employee: emp });
      setCredOpen(true);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Answered by /employees itself. It used to mean "fetch every user and look", which is
  // an Admin-only route: for HR Manager the call 403'd, the failure was swallowed, and
  // every row claimed nobody had a login.
  const hasLogin = (emp) => !!emp.has_login;

  // A code or a name is printed on a certificate and answered by the public verify page,
  // so a typo in one had to be correctable rather than retyped as a new person.
  const startEdit = (row) => setEditing({ row, form: { ...BLANK, ...Object.fromEntries(
    Object.keys(BLANK).map((k) => [k, row[k] ?? ""])) } });

  const saveEdit = async () => {
    const gaps = missingIn(editing.form);
    if (Object.keys(gaps).length) { editErr.report({ fields: gaps }); return; }
    setBusy(true);
    try {
      // The whole row goes back, not just the visible fields — the same reason the
      // masters edit does: PUT fills an unmentioned column from the model default.
      await api.put(`/employees/${editing.row.id}`, { ...editing.row, ...editing.form });
      toast.success("Employee updated");
      setEditing(null);
      editErr.clear();
      refresh();
    } catch (e) { editErr.report(e, { known: EMP_FIELDS }); }
    setBusy(false);
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/employees/${deleting.id}`);
      toast.success(`${deleting.full_name} retired`);
      setDeleting(null);
      refresh();
      if (retired) loadRetired();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not retire")); }
    setBusy(false);
  };

  const loadRetired = async () => {
    try {
      const r = await api.get("/employees", { params: { deleted: true } });
      if (!Array.isArray(r.data)) throw new Error("unexpected response");
      setRetired(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not load retired people")); }
  };

  const restore = async (row) => {
    try {
      await api.post(`/employees/${row.id}/restore`);
      toast.success(`${row.full_name} restored`);
      loadRetired();
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not restore")); }
  };

  // The selects narrow first; the text query then runs over what is left.
  const scoped = (emps || []).filter((e) => (unit === "all" || e.unit_id === unit)
                                         && (dept === "all" || e.department_id === dept));
  const filtered = useSearch(scoped, q, EMPLOYEE_FIELDS);
  // Sorted by the name a person actually reads in the cell, not by the id stored behind
  // it — sorting Department by uuid would look random.
  const sortCtx = useMemo(() => ({
    deptName: (id) => depts.find((d) => d.id === id)?.name || "",
    unitName: (id) => units.find((u) => u.id === id)?.name || "",
  }), [depts, units]);
  const sortColumns = useMemo(() => Object.fromEntries(
    Object.entries(EMP_SORT).map(([k, read]) => [
      k, typeof read === "function" ? (row) => read(row, sortCtx) : read])
  ), [sortCtx]);
  const sorted = useSorted(filtered, sortColumns, sort);

  const paged = usePaged(sorted);


  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Master data</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Employees</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {Array.isArray(emps) ? `${emps.length} people across all units.` : !loadError && "Loading..."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {writable && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-emp-btn"><Upload className="w-4 h-4 mr-1.5" /> Import Excel</Button>}
          <Button variant="outline" onClick={() => window.open(`${API}/exports/employees.xlsx`)} data-testid="export-emp-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
          {writable && <Button onClick={() => setOpen(true)} data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-1.5" /> Add employee</Button>}
        </div>
      </div>

      <Card className="p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <SearchInput className="md:col-span-2" value={q} onChange={setQ} testid="emp-search"
                       placeholder="Search name, code, designation or email..." />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All units</SelectItem>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All depts</SelectItem>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-zebra">
          <thead className="bg-muted/70 border-b border-border"><tr className="text-left">
            <SortableTh sortKey="code" sort={sort} onSort={setSort}>Code</SortableTh>
            <SortableTh sortKey="name" sort={sort} onSort={setSort}>Name</SortableTh>
            <SortableTh sortKey="designation" sort={sort} onSort={setSort}>Designation</SortableTh>
            <SortableTh sortKey="department" sort={sort} onSort={setSort}>Department</SortableTh>
            <SortableTh sortKey="unit" sort={sort} onSort={setSort}>Unit</SortableTh>
            <SortableTh sortKey="email" sort={sort} onSort={setSort}>Email</SortableTh>
            <SortableTh sortKey="login" sort={sort} onSort={setSort}>Login</SortableTh>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {paged.slice.map((e) => {
              const d = depts.find((x) => x.id === e.department_id);
              const u = units.find((x) => x.id === e.unit_id);
              const has = hasLogin(e);
              return (
                <tr key={e.id} onClick={() => setOpenId(e.id)} data-testid={`employee-row-${e.employee_code}`}
                    className={`border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer ${openId === e.id ? "bg-primary/5" : ""}`}>
                  <td className="px-4 py-2 font-mono">{e.employee_code}</td>
                  <td className="px-4 py-2 font-medium">{e.full_name}</td>
                  <td className="px-4 py-2">{e.designation}</td>
                  <td className="px-4 py-2">{d?.name}</td>
                  <td className="px-4 py-2">{u?.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{e.email}</td>
                  {/* The buttons act on the row without opening its panel. */}
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    {has ? <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500/40">Active</Badge> : (
                      <Button variant="ghost" size="sm" onClick={() => createLogin(e)} disabled={!e.email || !writable} data-testid={`create-login-${e.employee_code}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Create login
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => nav(`/employees/${e.id}`)} data-testid={`view-history-${e.employee_code}`}>History</Button>
                      {writable && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => startEdit(e)}
                                  aria-label={`Edit ${e.full_name}`} data-testid={`edit-emp-${e.employee_code}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleting(e)}
                                  aria-label={`Retire ${e.full_name}`} data-testid={`delete-emp-${e.employee_code}`}>
                            <Trash2 className="w-4 h-4 text-destructive-text" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {emps === null && loadError && (
              <tr><td colSpan={8} className="px-4 py-12 text-center" data-testid="employees-load-error">
                <div className="text-destructive-text">{loadError}</div>
                <Button variant="outline" size="sm" className="mt-3" onClick={load}>Try again</Button>
              </td></tr>
            )}
          </tbody>
          </table>
        </div>
        <Paginator paged={paged} label="people" />
      </Card>

      {writable && (
        <Card className="p-4 border border-border">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-semibold text-sm">Retired people</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Retiring hides somebody from every list; their training history and certificates stay.
              </p>
            </div>
            <Button variant="outline" size="sm" data-testid="toggle-retired"
                    onClick={() => (retired ? setRetired(null) : loadRetired())}>
              {retired ? "Hide" : "Show retired"}
            </Button>
          </div>
          {retired && (
            retired.length === 0
              ? <p className="text-sm text-muted-foreground mt-4">Nobody has been retired.</p>
              : <div className="mt-4 divide-y divide-border">
                  {retired.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2 gap-3">
                      <div className="text-sm">
                        <span className="font-mono text-xs text-muted-foreground mr-2">{r.employee_code}</span>
                        {r.full_name}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => restore(r)} data-testid={`restore-emp-${r.employee_code}`}>
                        <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Restore
                      </Button>
                    </div>
                  ))}
                </div>
          )}
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); editErr.clear(); } }}>
        <DialogContent className="max-w-2xl" data-testid="emp-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit employee</DialogTitle>
            <DialogDescription>
              {editing ? `${editing.row.employee_code} — this name and code appear on issued certificates.` : ""}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><FieldLabel htmlFor="edit-code" required>Employee code</FieldLabel><Input id="edit-code" value={editing.form.employee_code} onChange={(e) => setEdit({ employee_code: e.target.value })} data-testid="edit-emp-code" className={invalidClass(editErr.errors, "employee_code")} {...editErr.fieldProps("employee_code")} /><FieldError field="employee_code" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-name" required>Full name</FieldLabel><Input id="edit-name" value={editing.form.full_name} onChange={(e) => setEdit({ full_name: e.target.value })} data-testid="edit-emp-name" className={invalidClass(editErr.errors, "full_name")} {...editErr.fieldProps("full_name")} /><FieldError field="full_name" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-desig">Designation</FieldLabel><ComboBox id="edit-desig" testid="edit-desig" value={editing.form.designation} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, designation: v } })} options={hints.designations || []} placeholder="e.g. Sr. Engineer" /></div>
              <div><FieldLabel htmlFor="edit-email">Email</FieldLabel><Input id="edit-email" value={editing.form.email} onChange={(e) => setEdit({ email: e.target.value })} className={invalidClass(editErr.errors, "email")} {...editErr.fieldProps("email")} /><FieldError field="email" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-mobile">Mobile</FieldLabel><Input id="edit-mobile" value={editing.form.mobile} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, mobile: e.target.value } })} /></div>
              <div><FieldLabel htmlFor="edit-doj">Date of joining</FieldLabel><Input id="edit-doj" type="date" value={editing.form.date_of_joining || ""} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, date_of_joining: e.target.value } })} /></div>
              <div {...editErr.fieldProps("unit_id")}>
                <FieldLabel required>Unit</FieldLabel>
                <Select value={editing.form.unit_id} onValueChange={(v) => { editErr.clear("unit_id"); setEditUnit(v); }}><SelectTrigger data-testid="edit-emp-unit" aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
                <FieldError field="unit_id" errors={editErr.errors} />
              </div>
              <div {...editErr.fieldProps("department_id")}>
                <FieldLabel required>Department</FieldLabel>
                {(() => {
                  const options = departmentsInUnit(depts, editing.form.unit_id, editing.row.department_id);
                  return options.length === 0 ? (
                    <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5" data-testid="edit-emp-dept-empty">
                      {editing.form.unit_id ? "This unit has no departments yet — add one under Masters."
                                            : "Pick a unit first."}
                    </p>
                  ) : (
                    <Select value={editing.form.department_id} onValueChange={(v) => setEdit({ department_id: v })}><SelectTrigger data-testid="edit-emp-dept" aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger><SelectContent>{options.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
                  );
                })()}
                <FieldError field="department_id" errors={editErr.errors} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="emp-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent data-testid="emp-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retire "{deleting?.full_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              They leave every list, dropdown and nomination picker, and <b>their login is revoked
              immediately</b> &mdash; including any session they already have open. Attendance
              history and issued certificates are untouched and keep verifying. Refused only while
              they are still registered as a trainer. Restoring brings the person back but not the
              login; use "Create login" for that.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy}
                               className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               data-testid="emp-delete-confirm">Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) addErr.clear(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><FieldLabel htmlFor="emp-code" required>Employee code</FieldLabel><Input id="emp-code" value={f.employee_code} onChange={(e) => setAdd({ employee_code: e.target.value })} data-testid="emp-code" className={invalidClass(addErr.errors, "employee_code")} {...addErr.fieldProps("employee_code")} /><FieldError field="employee_code" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-name" required>Full name</FieldLabel><Input id="emp-name" value={f.full_name} onChange={(e) => setAdd({ full_name: e.target.value })} data-testid="emp-name" className={invalidClass(addErr.errors, "full_name")} {...addErr.fieldProps("full_name")} /><FieldError field="full_name" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-desig">Designation</FieldLabel><ComboBox id="emp-desig" testid="emp-desig" value={f.designation} onChange={(v) => setF({ ...f, designation: v })} options={hints.designations || []} placeholder="e.g. Sr. Engineer" /></div>
            <div><FieldLabel htmlFor="emp-email">Email</FieldLabel><Input id="emp-email" value={f.email} onChange={(e) => setAdd({ email: e.target.value })} className={invalidClass(addErr.errors, "email")} {...addErr.fieldProps("email")} /><FieldError field="email" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-mobile">Mobile</FieldLabel><Input id="emp-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
            <div><FieldLabel htmlFor="emp-doj">Date of joining</FieldLabel><Input id="emp-doj" type="date" value={f.date_of_joining} onChange={(e) => setF({ ...f, date_of_joining: e.target.value })} /></div>
            <div {...addErr.fieldProps("unit_id")}>
              <FieldLabel required>Unit</FieldLabel>
              <Select value={f.unit_id} onValueChange={(v) => { addErr.clear("unit_id"); setAddUnit(v); }}><SelectTrigger data-testid="emp-unit" aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
              <FieldError field="unit_id" errors={addErr.errors} />
            </div>
            <div {...addErr.fieldProps("department_id")}>
              <FieldLabel required>Department</FieldLabel>
              {(() => {
                const options = departmentsInUnit(depts, f.unit_id);
                return options.length === 0 ? (
                  <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5" data-testid="emp-dept-empty">
                    {f.unit_id ? "This unit has no departments yet — add one under Masters."
                               : "Pick a unit first — a department belongs to one."}
                  </p>
                ) : (
                  <Select value={f.department_id} onValueChange={(v) => setAdd({ department_id: v })}><SelectTrigger data-testid="emp-dept" aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger><SelectContent>{options.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
                );
              })()}
              <FieldError field="department_id" errors={addErr.errors} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="emp-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent data-testid="cred-dialog">
          <DialogHeader><DialogTitle>Employee login created</DialogTitle><DialogDescription>The password is shown once and cannot be retrieved afterwards.</DialogDescription></DialogHeader>
          {cred && (
            <div className="space-y-3 text-sm">
              <p>Share these credentials with <b>{cred.employee.full_name}</b>. They will be prompted to set a new password on first login.</p>
              <div className="p-3 rounded-md bg-muted border border-border font-mono text-sm space-y-1">
                <CopyableSecret label="Email" value={cred.email} testid="cred-email" />
                {cred.default_password && <CopyableSecret label="Password" value={cred.default_password} testid="cred-password" />}
                {cred.linked && <div className="text-xs text-muted-foreground">Existing user account linked — password unchanged.</div>}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setCredOpen(false)} data-testid="cred-close">Got it</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ImportEmployeesDialog open={importOpen} onOpenChange={setImportOpen} onImported={refresh} />

      <EmployeePeek id={openId} onClose={() => setOpenId("")} lookup={sortCtx}
                    onEdit={(row) => { setOpenId(""); startEdit({ ...row, has_login: row.has_login }); }} />
    </div>
  );
}
